import React from 'react'
import { useTheme } from 'styled-components/native'

import { useAuthContext } from 'features/auth/context/AuthContext'
import { isCurrentBeneficiary } from 'features/auth/helpers/checkStatusType'
import { renderInteractionTag } from 'features/offer/components/InteractionTag/InteractionTag'
import { OfferTile } from 'features/offer/components/OfferTile/OfferTile'
import { getIsAComingSoonOffer } from 'features/offer/helpers/getIsAComingSoonOffer'
import { OfferTileProps } from 'features/offer/types'
import { useFeatureFlag } from 'libs/firebase/firestore/featureFlags/useFeatureFlag'
import { RemoteStoreFeatureFlags } from 'libs/firebase/firestore/types'
import { formatPrice, getDisplayedPrice } from 'libs/parsers/getDisplayedPrice'
import { useCategoryHomeLabelMapping, useCategoryIdMapping } from 'libs/subcategories'
import { usePacificFrancToEuroRate } from 'queries/settings/useSettings'
import { useGetCurrencyToDisplay } from 'shared/currency/useGetCurrencyToDisplay'
import { getOfferDates } from 'shared/date/getOfferDates'
import { Offer } from 'shared/offer/types'
import { AB_TESTS } from 'shared/useABSegment/abTests'
import { useABSegment } from 'shared/useABSegment/useABSegment'

type Props = Omit<
  OfferTileProps,
  'offerLocation' | 'categoryLabel' | 'categoryId' | 'subcategoryId' | 'offerId' | 'price'
> & {
  item: Offer
  hasSmallLayout?: boolean
  containerWidth?: number
  withCenterAlign?: boolean
}

export const OfferTileWrapper = React.memo(function OfferTileWrapper(props: Props) {
  const { item, hasSmallLayout, withCenterAlign } = props
  const theme = useTheme()
  const { user } = useAuthContext()
  const currency = useGetCurrencyToDisplay()
  const { data: euroToPacificFrancRate } = usePacificFrancToEuroRate()
  const mapping = useCategoryIdMapping()
  const labelMapping = useCategoryHomeLabelMapping()
  const { subcategoryId, dates, releaseDate, isDuo, likes, chroniclesCount, name, thumbUrl } =
    item.offer
  const proAdvicesSegment = useABSegment(AB_TESTS.PRO_REVIEWS_ON_OFFER)
  const enableProAdvicesTag = useFeatureFlag(RemoteStoreFeatureFlags.WIP_PRO_REVIEWS_PLAYLIST)

  const formattedDate = getOfferDates({
    subcategoryId,
    dates,
    releaseDate,
    isPlaylist: true,
  })
  const formattedPrice = getDisplayedPrice(
    item.offer?.prices,
    currency,
    euroToPacificFrancRate,
    formatPrice({
      isDuo: !!(isDuo && isCurrentBeneficiary(user)),
    })
  )

  const tag = renderInteractionTag({
    theme,
    likesCount: likes,
    clubAdvicesCount: chroniclesCount,
    hasSmallLayout,
    isComingSoonOffer: getIsAComingSoonOffer(item.offer.bookingAllowedDatetime),
    subcategoryId: item.offer.subcategoryId,
    proAdvicesCount:
      enableProAdvicesTag && proAdvicesSegment === 'A' ? item.offer.proAdvicesCount : undefined,
  })

  return (
    <OfferTile
      offerLocation={item._geoloc}
      categoryLabel={labelMapping[subcategoryId]}
      categoryId={mapping[subcategoryId]}
      subcategoryId={subcategoryId}
      offerId={+item.objectID}
      name={name}
      date={formattedDate}
      thumbUrl={thumbUrl}
      price={formattedPrice}
      interactionTag={tag}
      {...props}
      withCenterAlign={withCenterAlign}
    />
  )
})
